import type { MeetingData, MeetingStatusPayloadData } from '../../types';

import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { App } from 'antd';
import { AxiosError } from 'axios';

import { axiosClient } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';

type Payload = {
  companyID: number;
  meetingID: number;
  body: MeetingStatusPayloadData | FormData;
};

export const updateMeetingStatus = async ({ companyID, meetingID, body }: Payload): Promise<MeetingData> => {
  const { data } = await axiosClient.put<MeetingData>(
    `/companies/${companyID}/meetings/${meetingID}/change-status`,
    body,
    body instanceof FormData
      ? {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      : undefined,
  );
  return data;
};

type UseUpdateMeetingStatusOptions = {
  config?: Partial<UseMutationOptions<MeetingData, AxiosError<{ message: string }>, Payload>>;
  postSuccess?: () => void;
};

export const useUpdateMeetingStatus = ({ config, postSuccess }: UseUpdateMeetingStatusOptions) => {
  const { notification: toaster } = App.useApp();
  return useMutation({
    onError: (error) => {
      toaster.error({
        message: 'Something Went Wrong',
        description: error.message,
      });
    },
    onSuccess: async (_, params) => {
      await queryClient.invalidateQueries({
        queryKey: ['meetings'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['meeting-details', params.companyID, params.meetingID],
      });
      toaster.success({
        message: 'Updated Successfully',
        description: 'Meeting Updated Successfully',
      });
      postSuccess?.();
    },
    ...config,
    mutationFn: updateMeetingStatus,
  });
};
